/**
 * useChatState Hook
 * 
 * Encapsulates all chat state management and event handlers for SessionChat.
 * Uses React Query for server state and a UI reducer for client state.
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useActivityTracking } from "@/hooks/useActivityTracking";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useGamification } from "@/hooks/useGamification";
import { useRealtimeChannel } from "@/hooks/useRealtimeChannel";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";
import { useReducerWithDevtools } from "@/lib/useReducerWithDevtools";
import {
  chatUIReducer,
  initialChatUIState,
  chatUIActions,
} from "@/reducers/chatReducer";
import { Message, Session } from "@/types/state";

interface UseChatStateProps {
  sessionId: string | undefined;
}

// --- React Query Keys ---
export const chatKeys = {
  all: (sessionId: string) => ['chat', sessionId] as const,
  session: (sessionId: string) => [...chatKeys.all(sessionId), 'session'] as const,
  messages: (sessionId: string) => [...chatKeys.all(sessionId), 'messages'] as const,
  branches: (sessionId: string) => [...chatKeys.all(sessionId), 'branches'] as const,
  bookmarks: (sessionId: string) => [...chatKeys.all(sessionId), 'bookmarks'] as const,
};

export function useChatState({ sessionId }: UseChatStateProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { updateStreak } = useActivityTracking();
  const { awardPoints } = useGamification();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const queryClient = useQueryClient();

  const [uiState, uiDispatch] = useReducerWithDevtools(
    chatUIReducer,
    initialChatUIState,
    'ChatUI'
  );
  const [titleGenerationAttempted, setTitleGenerationAttempted] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // --- Data Fetching with React Query ---

  const { data: session, isLoading: isLoadingSession } = useQuery({
    queryKey: chatKeys.session(sessionId!),
    queryFn: async () => {
      const { data } = await supabase.from("sessions").select("*").eq("id", sessionId!).single();
      return data as Session;
    },
    enabled: !!sessionId && !!user,
  });

  const { data: messages = [], isLoading: isLoadingMessages } = useQuery({
    queryKey: chatKeys.messages(sessionId!),
    queryFn: async () => {
      const { data } = await supabase.from("messages").select("*").eq("session_id", sessionId!).order("created_at", { ascending: true });
      return (data || []) as Message[];
    },
    enabled: !!sessionId && !!user,
  });

  const { data: branches = [], isLoading: isLoadingBranches } = useQuery({
    queryKey: chatKeys.branches(sessionId!),
    queryFn: async () => {
      const { data } = await supabase.rpc("get_branch_tree_with_stats", { p_session_id: sessionId! });
      return (data || []) as any as Session[];
    },
    enabled: !!session,
  });

  const { data: bookmarkedMessages = new Set<string>(), isLoading: isLoadingBookmarks } = useQuery({
    queryKey: chatKeys.bookmarks(sessionId!),
    queryFn: async () => {
      const { data: messageIdsData } = await supabase.from("messages").select("id").eq("session_id", sessionId!);
      const messageIds = messageIdsData?.map(m => m.id) || [];
      if (messageIds.length === 0) return new Set<string>();
      const { data: bookmarks } = await supabase.from("message_bookmarks").select("message_id").in("message_id", messageIds);
      return new Set(bookmarks?.map(b => b.message_id) || []);
    },
    enabled: !!sessionId && !!user,
  });

  const isLoading = isLoadingSession || isLoadingMessages || isLoadingBranches || isLoadingBookmarks;

  // --- Mutations ---

  const sendMessageMutation = useMutation({
    mutationFn: async (messageContent: string) => {
      await supabase.from("messages").insert({ session_id: sessionId, role: "user", content: messageContent });
      updateStreak();
      awardPoints('message');
      lastActivityRef.current = new Date();
      const { error } = await invoke("chat", { body: { sessionId, message: messageContent } });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(sessionId!) });
    },
    onError: (error: any) => {
      if (error.message?.includes("429")) {
        toast.error("Rate limit exceeded. Please wait before sending another message.");
      } else if (error.message?.includes("402")) {
        toast.error("AI credits depleted. Please add credits to continue.");
      } else {
        toast.error("Failed to send message");
      }
    },
  });

  const editMessageMutation = useMutation({
    mutationFn: async ({ messageId, newContent }: { messageId: string, newContent: string }) => {
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      if (messageIndex === -1) throw new Error("Message not found");

      const messagesToDelete = messages.slice(messageIndex + 1);
      if (messagesToDelete.length > 0) {
        await supabase.from("messages").delete().in("id", messagesToDelete.map((m) => m.id));
      }
      await supabase.from("messages").update({ content: newContent }).eq("id", messageId);
      const { error } = await invoke("chat", { body: { sessionId, message: newContent } });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(sessionId!) });
      uiDispatch(chatUIActions.cancelEditing());
      toast.success("Message updated and response regenerated");
    },
    onError: () => toast.error("Failed to edit message"),
  });

  const regenerateResponseMutation = useMutation({
    mutationFn: async (assistantMessage: Message) => {
      const messageIndex = messages.findIndex((m) => m.id === assistantMessage.id);
      if (messageIndex === -1) throw new Error("Message not found");
      let userMessageContent = "";
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          userMessageContent = messages[i].content;
          break;
        }
      }
      if (!userMessageContent) throw new Error("Could not find the original question");
      const messagesToDelete = messages.slice(messageIndex);
      await supabase.from("messages").delete().in("id", messagesToDelete.map((m) => m.id));
      const { error } = await invoke("chat", { body: { sessionId, message: userMessageContent } });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(sessionId!) });
      toast.success("Response regenerated");
    },
    onError: () => toast.error("Failed to regenerate response"),
    onSettled: () => uiDispatch(chatUIActions.setRegeneratingMessageId(null)),
  });

  const generateTitleMutation = useMutation({
    mutationFn: () => invoke("generate-session-title", { body: { sessionId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.session(sessionId!) });
      toast.success("Session title generated");
    },
    onError: () => toast.error("Failed to generate session title"),
  });

  const toggleBookmarkMutation = useMutation({
    mutationFn: async ({ messageId, isBookmarked }: { messageId: string, isBookmarked: boolean }) => {
      if (isBookmarked) {
        await supabase.from("message_bookmarks").delete().eq("message_id", messageId).eq("user_id", user!.id);
      } else {
        await supabase.from("message_bookmarks").insert({ message_id: messageId, user_id: user!.id });
      }
    },
    onSuccess: (_, { isBookmarked }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.bookmarks(sessionId!) });
      toast.success(isBookmarked ? "Bookmark removed" : "Message bookmarked");
    },
    onError: () => toast.error("Failed to update bookmark"),
  });

  const createBranchMutation = useMutation({
    mutationFn: async ({ branchName, branchingFromMessage }: { branchName: string, branchingFromMessage: Message }) => {
      const branchPointIndex = messages.findIndex(m => m.id === branchingFromMessage.id);
      const messagesUpToBranch = messages.slice(0, branchPointIndex + 1);
      const { data: newSession } = await supabase.from("sessions").insert({ name: session!.name, branch_name: branchName, project_id: session!.project_id, parent_session_id: session!.id, is_branch: true, branched_at_message_id: branchingFromMessage.id }).select().single();
      if (!newSession) throw new Error("Failed to create new session for branch");
      const messagesToCopy = messagesUpToBranch.map(m => ({ session_id: newSession.id, role: m.role, content: m.content, created_at: m.created_at }));
      await supabase.from("messages").insert(messagesToCopy);
      return newSession;
    },
    onSuccess: (newSession) => {
      toast.success(`Branch "${newSession.branch_name}" created`);
      navigate(`/sessions/${newSession.id}`);
    },
    onError: () => toast.error("Failed to create branch"),
  });

  const archiveBranchMutation = useMutation({
    mutationFn: async ({ branchId, reason }: { branchId: string, reason?: string }) => {
      const { data, error } = await supabase.rpc("archive_session", { p_session_id: branchId, p_reason: reason });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.branches(sessionId!) });
      toast.success("Branch archived");
    },
    onError: () => toast.error("Failed to archive branch"),
  });

  const unarchiveBranchMutation = useMutation({
    mutationFn: async (branchId: string) => {
      const { data, error } = await supabase.rpc("unarchive_session", { p_session_id: branchId });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.branches(sessionId!) });
      toast.success("Branch restored");
    },
    onError: () => toast.error("Failed to restore branch"),
  });

  const analyzeMergeMutation = useMutation({
    mutationFn: (sourceSessionId: string) => invoke("analyze-merge-conflicts", { body: { sourceSessionId, targetSessionId: sessionId! } }),
  });

  const initiateMergeMutation = useMutation({
    mutationFn: async (variables: { strategy: "merge_into_existing" | "create_new_branch", resolutions: Record<number, "source" | "target" | "both" | "neither">, newSessionName?: string }) => {
      const { strategy, resolutions, newSessionName } = variables;
      const { data: mergeRecord } = await supabase.from("session_merges").insert({ source_session_id: uiState.selectedMergeBranch!.id, target_session_id: strategy === "merge_into_existing" ? sessionId : null, merge_type: strategy, user_resolutions: resolutions }).select().single();
      if (!mergeRecord) throw new Error("Failed to create merge record");
      const { data: mergeResult } = await supabase.rpc("execute_branch_merge", { p_merge_id: mergeRecord.id, p_resolved_conflicts: resolutions, p_new_session_name: newSessionName });
      const result = mergeResult as { success: boolean; error?: string; new_session_id?: string };
      if (!result.success) throw new Error(result.error || "Merge failed");
      return result;
    },
    onSuccess: (result) => {
      toast.success("Branches merged successfully!");
      if (result.new_session_id) {
        navigate(`/sessions/${result.new_session_id}`);
      } else {
        queryClient.invalidateQueries({ queryKey: chatKeys.all(sessionId!) });
      }
    },
    onError: () => toast.error("Failed to merge branches"),
  });

  // --- Real-time Subscriptions ---

  useRealtimeChannel({
    channelName: `messages:${sessionId}`,
    type: 'postgres_changes',
    event: 'INSERT',
    schema: 'public',
    table: 'messages',
    filter: `session_id=eq.${sessionId}`,
    onMessage: () => queryClient.invalidateQueries({ queryKey: chatKeys.messages(sessionId!) }),
  });

  useRealtimeChannel({
    channelName: `session:${sessionId}`,
    type: 'postgres_changes',
    event: 'UPDATE',
    schema: 'public',
    table: 'sessions',
    filter: `id=eq.${sessionId}`,
    onMessage: () => queryClient.invalidateQueries({ queryKey: chatKeys.session(sessionId!) }),
  });

  // --- Effects ---

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate(ROUTES.AUTH);
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!session || titleGenerationAttempted) return;
    const userMessages = messages.filter(m => m.role === "user");
    const isDefaultTitle = session.name.startsWith("Session from") || /^Session \d+$/.test(session.name);
    if (isDefaultTitle && userMessages.length >= 3) {
      setTitleGenerationAttempted(true);
      generateTitleMutation.mutate();
    }
  }, [messages, session, titleGenerationAttempted, generateTitleMutation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // --- UI Handlers ---
  const setInput = useCallback((value: string) => uiDispatch(chatUIActions.setInput(value)), []);
  const setEditingContent = useCallback((value: string) => uiDispatch(chatUIActions.updateEditingContent(value)), []);
  const toggleBookmarkPanel = useCallback(() => uiDispatch(chatUIActions.toggleBookmarkPanel()), []);
  const toggleFrameworkWidget = useCallback(() => uiDispatch(chatUIActions.toggleFrameworkWidget()), []);
  const toggleBranchList = useCallback(() => uiDispatch(chatUIActions.toggleBranchList()), []);
  const setBranchDialogOpen = useCallback((open: boolean) => { if (!open) uiDispatch(chatUIActions.cancelBranching()); }, []);
  const setMergeDialogOpen = useCallback((open: boolean) => { if (!open) uiDispatch(chatUIActions.cancelMerging()); }, []);
  const startEditingMessage = useCallback((message: Message) => uiDispatch(chatUIActions.startEditing(message.id, message.content)), []);
  const cancelEditing = useCallback(() => uiDispatch(chatUIActions.cancelEditing()), []);
  const startRegenerateResponse = useCallback((message: Message) => uiDispatch(chatUIActions.startRegenerating(message)), []);
  const cancelRegenerating = useCallback(() => uiDispatch(chatUIActions.cancelRegenerating()), []);
  const handleBranchFromMessage = useCallback((message: Message) => uiDispatch(chatUIActions.startBranching(message)), []);
  const handleMergeBranch = useCallback((branch: Session) => uiDispatch(chatUIActions.startMerging(branch)), []);
  const injectFramework = useCallback((frameworkPrompt: string) => {
    setInput(frameworkPrompt);
    toggleFrameworkWidget();
  }, [setInput, toggleFrameworkWidget]);

  const sendMessage = useCallback(() => {
    if (!uiState.input.trim()) return;
    sendMessageMutation.mutate(uiState.input.trim());
    setInput("");
  }, [uiState.input, sendMessageMutation, setInput]);

  const saveEditedMessage = useCallback((messageId: string) => {
    if (!uiState.editingContent.trim()) return;
    editMessageMutation.mutate({ messageId, newContent: uiState.editingContent.trim() });
  }, [uiState.editingContent, editMessageMutation]);

  const regenerateResponse = useCallback(() => {
    if (!uiState.messageToRegenerate) return;
    regenerateResponseMutation.mutate(uiState.messageToRegenerate);
  }, [uiState.messageToRegenerate, regenerateResponseMutation]);

  const toggleBookmark = useCallback((messageId: string) => {
    toggleBookmarkMutation.mutate({ messageId, isBookmarked: bookmarkedMessages.has(messageId) });
  }, [bookmarkedMessages, toggleBookmarkMutation]);

  const createBranch = useCallback((branchName: string) => {
    if (!uiState.branchingFromMessage) return;
    createBranchMutation.mutate({ branchName, branchingFromMessage: uiState.branchingFromMessage });
  }, [uiState.branchingFromMessage, createBranchMutation]);

  const handleSwitchBranch = useCallback((branchId: string) => navigate(`/sessions/${branchId}`), [navigate]);
  const handleArchiveBranch = useCallback((branchId: string, reason?: string) => archiveBranchMutation.mutate({ branchId, reason }), [archiveBranchMutation]);
  const handleUnarchiveBranch = useCallback((branchId: string) => unarchiveBranchMutation.mutate(branchId), [unarchiveBranchMutation]);
  const handleAnalyzeMerge = useCallback(async () => {
    if (!uiState.selectedMergeBranch) throw new Error("No branch selected for merge analysis");
    const result = await analyzeMergeMutation.mutateAsync(uiState.selectedMergeBranch.id);
    return result.data;
  }, [uiState.selectedMergeBranch, analyzeMergeMutation]);
  const handleInitiateMerge = useCallback((...args: Parameters<typeof initiateMergeMutation.mutate>) => initiateMergeMutation.mutate(...args), [initiateMergeMutation]);
  const jumpToMessage = useCallback((messageId: string) => {
    const element = messageRefs.current[messageId];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("ring-2", "ring-primary");
      setTimeout(() => {
        element.classList.remove("ring-2", "ring-primary");
      }, 2000);
    }
  }, []);

  return {
    // State
    session,
    messages,
    branches,
    bookmarkedMessages,
    isLoading,
    uiState,
    
    // Refs
    messagesEndRef,
    messageRefs,
    
    // Handlers
    sendMessage,
    saveEditedMessage,
    regenerateResponse,
    toggleBookmark,
    createBranch,
    handleBranchFromMessage,
    handleSwitchBranch,
    handleArchiveBranch,
    handleUnarchiveBranch,
    handleMergeBranch,
    handleAnalyzeMerge,
    handleInitiateMerge,
    injectFramework,
    setInput,
    setEditingContent,
    toggleBookmarkPanel,
    toggleFrameworkWidget,
    toggleBranchList,
    setBranchDialogOpen,
    setMergeDialogOpen,
    startEditingMessage,
    cancelEditing,
    startRegenerateResponse,
    cancelRegenerating,
    jumpToMessage,
    isTitleGenerating: generateTitleMutation.isPending,
  };
}
